import React, { useMemo } from "react";
import { MoreVertical } from "react-feather";
import { ActionMenu } from "../../molecules/ActionMenu";
import { defineMenu } from "../../registry";
import type { MenuItemConfig } from "../../registry";
import { usePortalMenu } from "../../../hooks/usePortalMenu";
import type { Delivery, DeliveryStatus } from "./types";
import { IconButton } from "./ActiveDeliveriesTable.styles";

export type DeliveryRowAction = "edit" | "pause" | "resume" | "cancel" | "delete";

/** Status decides which actions make sense — e.g. a cancelled delivery can't be paused or cancelled again. Edit only when offered. */
const buildDeliveryRowMenu = (status: DeliveryStatus, canEdit: boolean) => {
  const items: MenuItemConfig<DeliveryRowAction>[] = canEdit ? [{ id: "edit", label: "Edit" }] : [];

  if (status === "paused") {
    items.push({ id: "resume", label: "Resume delivery" });
  } else if (status === "active") {
    items.push({ id: "pause", label: "Pause delivery" });
  }

  if (status === "active" || status === "paused") {
    items.push({ id: "cancel", label: "Cancel delivery" });
  }

  items.push({ id: "delete", label: "Delete", danger: true });

  return defineMenu(items);
};

export interface DeliveryRowActionsMenuProps {
  delivery: Delivery;
  isOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
  onEdit?: (id: string) => void;
  onPause?: (id: string) => void;
  onResume?: (id: string) => void;
  onCancel?: (id: string) => void;
  onDelete?: (id: string) => void;
}

export const DeliveryRowActionsMenu: React.FC<DeliveryRowActionsMenuProps> = ({
  delivery,
  isOpen,
  onToggle,
  onClose,
  onEdit,
  onPause,
  onResume,
  onCancel,
  onDelete,
}) => {
  const { triggerRef, menuRef, rect } = usePortalMenu<HTMLButtonElement, HTMLUListElement>({
    open: isOpen,
    onOutsideClick: onClose,
  });

  const canEdit = Boolean(onEdit) && delivery.editable !== false;
  const menu = useMemo(() => buildDeliveryRowMenu(delivery.status, canEdit), [delivery.status, canEdit]);

  const handleAction = (action: DeliveryRowAction) => {
    switch (action) {
      case "edit":
        onEdit?.(delivery.id);
        break;
      case "pause":
        onPause?.(delivery.id);
        break;
      case "resume":
        onResume?.(delivery.id);
        break;
      case "cancel":
        onCancel?.(delivery.id);
        break;
      case "delete":
        onDelete?.(delivery.id);
        break;
    }
  };

  return (
    <ActionMenu
      items={menu}
      onAction={handleAction}
      isOpen={isOpen}
      onClose={onClose}
      menuRef={menuRef}
      menuRect={rect}
      ariaLabel="Delivery actions"
      align="right"
      trigger={
        <IconButton
          ref={triggerRef}
          type="button"
          aria-label="Delivery actions"
          aria-haspopup="menu"
          aria-expanded={isOpen}
          onClick={(e) => {
            e.stopPropagation();
            onToggle();
          }}
        >
          <MoreVertical size={18} />
        </IconButton>
      }
    />
  );
};

export default DeliveryRowActionsMenu;
